import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepButton from '@material-ui/core/StepButton';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import MobileStepper from '@material-ui/core/MobileStepper';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import filter from 'lodash/filter'
import reject from 'lodash/reject'
import find from 'lodash/find'

import LicenseCountForm from './LicenseCountForm'
import PageFormatForm from './PageFormatForm'
import DisplayOptionsForm from './DisplayOptionsForm';
import BillingRatesForm from './BillingRatesForm';
import RepairDiscountForm from './RepairDiscountForm';
import PartMarkupForm from './PartMarkupForm';
import RepairGroupsForm from './RepairGroupsForm';
import SalesTaxForm from './SalesTaxForm';
import EquipmentBrandForm from './EquipmentBrandForm';
import SpecialInstructionsForm from './SpecialInstructionsForm';



const styles = theme => ({
  root: {
    width: '100%',
  },
  smallScreenRoot: {
    marginBottom: 48,
  },
  button: {
    marginTop: theme.spacing.unit,
    marginRight: theme.spacing.unit,
  },
  actionsContainer: {
    marginBottom: theme.spacing.unit * 2,
    textAlign: 'right',
  },
  resetContainer: {
    padding: theme.spacing.unit * 3,
  },
  stepper: {
    padding: 0,
  },
});


const STEPS = [
  { name: 'Licenses',               content: LicenseCountForm,                            pricingMode: ['Legacy'],                       validate: []},
  { name: 'Page Format',            content: PageFormatForm,                              pricingMode: ['Legacy'],                       validate: []},
  { name: 'Display Options',        content: DisplayOptionsForm,                          pricingMode: ['Legacy', 'Safe', 'Simplified'], validate: []},
  { name: 'Billing Rates',          content: BillingRatesForm,                            pricingMode: ['Legacy', 'Safe'],               validate: ['regularLaborRate', 'overtimeLaborRate']},
  { name: 'Discounts',              content: RepairDiscountForm,                          pricingMode: ['Legacy', 'Safe'],               validate: ['regularDiscountAmount', 'regularDiscountType', 'overtimeDiscountAmount', 'overtimeDiscountType']},
  { name: 'Parts Markup',           content: PartMarkupForm,                              pricingMode: ['Legacy', 'Safe', 'Simplified'], validate: ['partsMarkupTable']},
  { name: 'Sales Tax',              content: SalesTaxForm,                                pricingMode: ['Legacy'],                       validate: ['laborTaxRate', 'partsTaxRate', 'partsTaxCalculation']},
  { name: 'Equipment Brands',       content: EquipmentBrandForm,        hvacOnly: true,   pricingMode: ['Legacy', 'Safe', 'Simplified'], validate: ['oems']},
  { name: 'Repair Groups',          content: RepairGroupsForm,          hvacOnly: true,   pricingMode: ['Legacy', 'Safe', 'Simplified'], validate: []},
  { name: 'Special Instructions',   content: SpecialInstructionsForm,                     pricingMode: ['Legacy', 'Safe', 'Simplified'], validate: []},
]

class BookConfig extends React.Component {
  state = {
    highlightErrors: false,
  };

  getStepContent = (step) => {
    return React.createElement(
      this.getSteps()[step].content,
      {
        order: this.props.order,
        actions: this.props.actions,
        showErrors: this.state.highlightErrors,
        smallScreen: this.props.smallScreen,
        pricingMode: this.props.pricingMode,
      },
      null
    )
  }

  stepHasErrors = () => {
    const {bookConfigStep} = this.props
    const validationFields = this.getSteps()[bookConfigStep].validate
    const {_errors} = this.props.order
    return !!find(validationFields, f => _errors[f])
  }

  // TODO: Show error messages, instead of just highlighting the fields in red
  handleNext = () => {
    if (this.stepHasErrors()) {
      this.setState({highlightErrors: true})
      return
    } else {
      this.setState({highlightErrors: false})
    }

    const {bookConfigStep, onClose, actions, order} = this.props
    if (bookConfigStep === this.getSteps().length - 1) {
      actions.flagOrderAsConfigured(order)
      onClose()
    } else {
      actions.gotoBookConfigStep(bookConfigStep + 1)
    }
  };

  handleBack = () => {
    const {bookConfigStep} = this.props
    this.props.actions.gotoBookConfigStep(bookConfigStep - 1)
  };

  getSteps = () => {
    var steps = STEPS
    if (this.props.pricingMode) steps = filter(steps, {'pricingMode': [this.props.pricingMode]})
    if (this.props.order.tradeCode === 'HVC') {
      return steps
    } else {
      return reject(steps, 'hvacOnly')
    }
  }

  fireHandleStep = step => () => {
    this.props.actions.gotoBookConfigStep(step)
  };

  render = () => {
    const { classes, smallScreen, bookConfigStep, order } = this.props;

    if (!order) return null

    const hasErrors = this.stepHasErrors()

    const nextButtonVariant = hasErrors ? 'outlined' : 'contained'

    const isLastStep = bookConfigStep === this.getSteps().length - 1
    const nextButtonLabel = isLastStep ? 'Finish' : 'Next'

    return (
      <div className={classes.root + ' ' + (smallScreen ? classes.smallScreenRoot : '')}>
        {
          !smallScreen
          ? (
            <Stepper className={classes.stepper} activeStep={bookConfigStep} >
              {this.getSteps().map((step, index) => (
                <Step key={step.name}>
                  <StepButton
                    onClick={this.fireHandleStep(index)}
                    disabled={false}
                  >
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          )
          : null
        }

        <Typography variant="h6" style={{margin: '1em 0'}}>
          {this.getSteps()[bookConfigStep].name}
        </Typography>

        {this.getStepContent(bookConfigStep)}

        {
          smallScreen
          ?
          (
            <MobileStepper
              variant="dots"
              steps={this.getSteps().length}
              position="bottom"
              activeStep={bookConfigStep}
              nextButton={
                <Button variant={nextButtonVariant} color="primary" size="small" onClick={this.handleNext}>
                  {nextButtonLabel} <KeyboardArrowRight />
                </Button>
              }
              backButton={
                <Button variant="outlined" size="small" onClick={this.handleBack} disabled={bookConfigStep === 0}>
                  <KeyboardArrowLeft /> Back
                </Button>
              }
            />
          )
          :
          (
            <React.Fragment>
              <div className={classes.actionsContainer}>
                <div>
                  <Button
                    disabled={bookConfigStep === 0}
                    onClick={this.handleBack}
                    className={classes.button}
                  >
                    Back
                  </Button>
                  <Button
                    variant={nextButtonVariant}
                    color="primary"
                    onClick={this.handleNext}
                    className={classes.button}
                  >
                    {nextButtonLabel}
                  </Button>
                </div>
              </div>
            </React.Fragment>
          )
        }

      </div>
    );
  }
}

BookConfig.propTypes = {
  classes: PropTypes.object,
  smallScreen: PropTypes.bool,
  order: PropTypes.object,
  actions: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  bookConfigStep: PropTypes.number,
  pricingMode: PropTypes.string,
};

export default withStyles(styles)(BookConfig);
